import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { hydraSupplierConfigMap } from 'src/environments/hydra/config.map';
import { features, FeatureKey } from '../../helpers/tenant.features';
import { FeatureFlagsService } from '../../services/featureFlags.service';

// Dev/QA overlay for the runtime flag state (MDG-105). Hidden unless
// localStorage.hydraDebug === '1'; `?hydraDebug=1` / `?hydraDebug=0` set or clear it.
const STORAGE_KEY = 'hydraDebug';
const QUERY_KEY = 'hydraDebug';
const GROUPED_FLAGS = ['ui', 'process'] as const;
const BACKEND_META_KEY = '_hydra';

type FlagValue = unknown;
type FlagRecord = Record<string, FlagValue>;

interface FlagRow {
  readonly key: string;
  readonly value: string;
  readonly bool?: boolean;
  readonly source: 'namespace' | 'default' | 'raw';
}

interface BackendMeta {
  readonly namespace: string | null;
}

interface OverlayInfo {
  readonly flagsState: 'loaded' | 'missing';
  readonly namespace: string;
  readonly buildNamespace: string;
  readonly host: string;
  readonly hostMapped: boolean;
  readonly routeFeature: string;
  readonly backend: BackendMeta | null;
  readonly namespaceMismatch: boolean;
}

const EMPTY_INFO: OverlayInfo = {
  flagsState: 'missing',
  namespace: '',
  buildNamespace: '',
  host: '',
  hostMapped: false,
  routeFeature: '',
  backend: null,
  namespaceMismatch: false,
};

function isRecord(value: unknown): value is FlagRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Booleans render as on/off pills; everything else as text or pretty JSON.
function toRow(key: string, value: FlagValue, source: FlagRow['source']): FlagRow {
  return typeof value === 'boolean'
    ? { key, value: String(value), bool: value, source }
    : { key, value: stringify(value), source };
}

function stringify(value: FlagValue): string {
  return isRecord(value) || Array.isArray(value)
    ? JSON.stringify(value, null, 2)
    : String(value);
}

@Component({
  selector: 'app-hydra-debug',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div *ngIf="visible" class="hydra-debug" data-testid="hydra-debug">
      <button
        type="button"
        class="pill"
        [class.on]="info.flagsState === 'loaded'"
        (click)="toggle()"
        data-testid="hydra-debug-toggle"
      >
        flags: {{ info.flagsState }}
      </button>

      <div *ngIf="expanded" class="panel">
        <div class="actions">
          <input
            type="text"
            placeholder="filter flags"
            [value]="filterText"
            (input)="onFilter($event)"
            data-testid="hydra-debug-filter"
          />
          <button type="button" (click)="reload()">reload</button>
          <button type="button" (click)="copy()">copy</button>
          <button type="button" (click)="hide()">hide</button>
        </div>

        <table class="kv">
          <tr><th colspan="2">tenant</th></tr>
          <tr><td>flags</td><td>{{ info.flagsState }}</td></tr>
          <tr><td>namespace (flags resolve against)</td><td>{{ info.namespace }}</td></tr>
          <tr>
            <td>build clientNameSpace</td>
            <td [class.warn]="info.namespaceMismatch">
              {{ info.buildNamespace }}
              {{ info.namespaceMismatch ? '⚠ differs — backend namespace wins' : '' }}
            </td>
          </tr>
          <tr>
            <td>host</td>
            <td [class.warn]="!info.hostMapped">
              {{ info.host }} {{ info.hostMapped ? '' : '(unmapped → default)' }}
            </td>
          </tr>
          <tr><td>route requiresFeature</td><td>{{ info.routeFeature }}</td></tr>

        </table>

        <table class="kv">
          <tr><th colspan="2">flags ({{ visibleRows.length }})</th></tr>
          <tr *ngFor="let row of visibleRows; trackBy: trackRow">
            <td>{{ row.key }}</td>
            <td>
              <span *ngIf="row.bool !== undefined" class="bool" [class.on]="row.bool">{{ row.value }}</span>
              <pre *ngIf="row.bool === undefined" class="val">{{ row.value }}</pre>
              <span *ngIf="row.source !== 'raw'" class="src">({{ row.source }})</span>
            </td>
          </tr>
        </table>

        <table class="kv">
          <tr><th colspan="2">features</th></tr>
          <tr *ngFor="let row of featureRows; trackBy: trackRow">
            <td>{{ row.key }}</td>
            <td><span class="bool" [class.on]="row.bool">{{ row.value }}</span></td>
          </tr>
        </table>
      </div>
    </div>
  `,
  styles: [
    `
      .hydra-debug { position: fixed; left: 12px; bottom: 12px; z-index: 99999; font: 12px/1.4 monospace; }
      .pill { border: 0; border-radius: 12px; padding: 4px 10px; background: #666; color: #fff; cursor: pointer; }
      .pill.on { background: #2a7; }
      .panel { margin-top: 6px; width: 680px; max-width: calc(100vw - 24px); max-height: 60vh; overflow: auto; background: #1e1e1e; color: #ddd; border-radius: 6px; padding: 8px; box-shadow: 0 2px 12px rgba(0,0,0,.4); }
      .actions { display: flex; gap: 6px; margin-bottom: 6px; }
      .actions input { flex: 1; font: inherit; padding: 2px 6px; }
      .actions button { font: inherit; padding: 2px 8px; cursor: pointer; }
      .kv { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
      .kv th { text-align: left; color: #9cf; padding: 4px 0; border-bottom: 1px solid #444; }
      .kv td { padding: 2px 4px; vertical-align: top; border-bottom: 1px solid #2a2a2a; word-break: break-all; }
      .kv td:first-child { color: #aaa; white-space: nowrap; width: 38%; }
      .val { margin: 0; font: inherit; white-space: pre-wrap; word-break: break-word; }
      .warn { color: #f9a825; }
      .src { color: #888; }
      .bool { display: inline-block; min-width: 42px; text-align: center; border-radius: 10px; padding: 0 8px; background: #555; color: #ddd; }
      .bool.on { background: #2a7; color: #fff; }
    `,
  ],
})
export class HydraDebugComponent implements OnInit, OnDestroy {
  visible = false;
  expanded = false;
  filterText = '';
  info: OverlayInfo = EMPTY_INFO;
  visibleRows: readonly FlagRow[] = [];
  featureRows: readonly FlagRow[] = [];

  private rows: readonly FlagRow[] = [];
  private navigation?: Subscription;

  constructor(
    private readonly featureFlags: FeatureFlagsService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyQueryToggle();
    this.visible = this.readStorage();
    if (!this.visible) {
      return;
    }
    this.refresh();
    this.navigation = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    this.navigation?.unsubscribe();
  }

  toggle(): void {
    this.expanded = !this.expanded;
    if (this.expanded) {
      this.refresh();
    }
  }

  hide(): void {
    this.writeStorage(false);
    this.visible = false;
    this.cdr.markForCheck();
  }

  onFilter(event: Event): void {
    this.filterText = (event.target as HTMLInputElement).value;
    this.applyFilter();
    this.cdr.markForCheck();
  }

  async reload(): Promise<void> {
    await this.featureFlags.getFeatureFlags();
    this.refresh();
  }

  copy(): void {
    const payload = { info: this.info, flags: this.rows };
    navigator.clipboard?.writeText(JSON.stringify(payload, null, 2));
  }

  trackRow(_index: number, row: FlagRow): string {
    return row.key;
  }

  private refresh(): void {
    this.info = this.buildInfo();
    this.rows = this.buildRows();
    this.featureRows = (Object.keys(features) as FeatureKey[])
      .sort()
      .map(key => toRow(key, features[key], 'raw'));
    this.applyFilter();
    this.cdr.markForCheck();
  }

  private applyFilter(): void {
    const needle = this.filterText.trim().toLowerCase();
    this.visibleRows = needle
      ? this.rows.filter(row => row.key.toLowerCase().includes(needle))
      : this.rows;
  }

  private buildInfo(): OverlayInfo {
    const all = this.featureFlags.getAll();
    const namespace = this.featureFlags.namespace;
    const buildNamespace = environment.supplierConfig.clientNameSpace;
    const backend = this.readBackendMeta(all);
    return {
      flagsState: all ? 'loaded' : 'missing',
      namespace,
      buildNamespace,
      host: window.location.host,
      hostMapped: window.location.host in hydraSupplierConfigMap,
      routeFeature: this.readRouteFeature(),
      backend,
      namespaceMismatch: buildNamespace !== namespace,
    };
  }

  private readBackendMeta(all: unknown): BackendMeta | null {
    if (!isRecord(all) || !isRecord(all[BACKEND_META_KEY])) {
      return null;
    }
    const meta = all[BACKEND_META_KEY];
    return {
      namespace: typeof meta['namespace'] === 'string' ? meta['namespace'] : null,
    };
  }

  private readRouteFeature(): string {
    let leaf = this.route.snapshot;
    while (leaf.firstChild) {
      leaf = leaf.firstChild;
    }
    const feature = leaf.data?.['requiresFeature'];
    return feature === undefined ? 'unset (allowed)' : String(feature);
  }

  private buildRows(): FlagRow[] {
    const all = this.featureFlags.getAll();
    if (!isRecord(all)) {
      return [];
    }
    const namespace = this.featureFlags.namespace;
    const grouped = ([] as FlagRow[]).concat(
      ...GROUPED_FLAGS.map(group => this.groupRows(group, all[group], namespace)),
    );
    const raw = Object.keys(all)
      .filter(key => !GROUPED_FLAGS.includes(key as typeof GROUPED_FLAGS[number]))
      .filter(key => key !== BACKEND_META_KEY)
      .sort()
      .map(key => toRow(key, all[key], 'raw'));
    return [...grouped, ...raw];
  }

  private groupRows(group: string, config: unknown, namespace: string): FlagRow[] {
    if (!isRecord(config)) {
      return [];
    }
    const defaults: FlagRecord = isRecord(config['default']) ? config['default'] : {};
    const nsBlock = config[namespace];
    const overrides: FlagRecord = isRecord(nsBlock) ? nsBlock : {};
    return Object.keys({ ...defaults, ...overrides })
      .sort()
      .map(subkey =>
        toRow(
          `${group}.${subkey}`,
          subkey in overrides ? overrides[subkey] : defaults[subkey],
          subkey in overrides ? 'namespace' : 'default',
        ),
      );
  }

  private applyQueryToggle(): void {
    const value = new URLSearchParams(window.location.search).get(QUERY_KEY);
    if (value === '1' || value === '0') {
      this.writeStorage(value === '1');
    }
  }

  private readStorage(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private writeStorage(on: boolean): void {
    try {
      if (on) {
        localStorage.setItem(STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // storage blocked: the overlay simply stays hidden
    }
  }
}
